import type { CSSProperties } from 'react'
import { faCheck, faMinus } from '@fortawesome/pro-regular-svg-icons'
import { Icon, Table, Text } from '@/ui'
import { ProductFeatureId } from '@/actions/permissionActions'
import { mtc, TypographyStyles } from '@/styles'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { RECOMMENDED_BADGE_COPY } from '@/features/compliance/plan-pricing/recommendedPlan.copy'
import { planChangeFeatureLabel } from './lib/entitlementCopy'
import { displayRowsFor } from './lib/taxFilingRows'
import {
  CADENCE_LABEL,
  CADENCE_PERIOD,
  CADENCE_SUFFIX,
  TIER_DISPLAY_NAMES,
  useAudienceEyebrows,
  useBaseTierDisplayNames,
  type TierDisplayNames,
  compareTierRank,
  isLegacyPlan,
  normalizeAmountToCadence,
} from './lib/helpers'
import type {
  CurrentPlan,
  PlanCadence,
  PlanCatalog,
  PlanCatalogTier,
  PlanChangeTarget,
  PlanChangeTier,
} from './lib/planChange.types'

export interface ComparisonTableProps {
  catalog: PlanCatalog
  currentPlan: CurrentPlan
  targets: PlanChangeTarget[]
  cadence: PlanCadence
  /** Rows the comparison cannot state truthfully for this customer. */
  hideRows?: string[]
  /** The S corp service a column offers this customer, or null where none applies. */
  sCorpServiceFor?: (tier: string, isCurrentPlan: boolean) => string | null
  recommendedLookupKeys?: ReadonlySet<string>
}

interface TierColumn {
  key: string
  tier: PlanChangeTier
  displayName: string
  featureIds: string[]
  isCurrentPlan: boolean
  eyebrow: string | null
  isRecommended: boolean
  offered: boolean
  listAmountCents: number | null
  shownAmountCents: number | null
  billedNote: string | null
  isPromoRate: boolean
}

const TABLE_MIN_WIDTH =
  'min-w-[calc(140px+var(--plan-columns)*150px)] @xl:min-w-[calc(280px+var(--plan-columns)*150px)]'

/** Pins the Feature column while the plan columns scroll sideways on narrow containers. */
const STICKY_FEATURE_CELL =
  'sticky left-0 z-10 @max-xl:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]'

const currentTint = 'bg-neutral-25'

const strikesList = (column: TierColumn): boolean =>
  column.shownAmountCents !== null &&
  column.listAmountCents !== null &&
  column.shownAmountCents < column.listAmountCents

/** Holds a row open in the cells that have nothing for it, so the price itself stays level. */
const PriceRowSpacer = () => (
  <Text variant={TypographyStyles.BODY_XS} aria-hidden className="invisible">
    &nbsp;
  </Text>
)

/** A plan billed on another schedule is never one of the `targets`, so nothing else emits it. */
const currentPlanColumn = (
  tier: Pick<PlanCatalogTier, 'tier' | 'featureIds'>,
  currentPlan: CurrentPlan,
  cadence: PlanCadence,
  legacy: boolean,
  names: TierDisplayNames
): TierColumn => {
  const projected = cadence !== currentPlan.cadence
  const restate = (amountCents: number): number =>
    projected
      ? normalizeAmountToCadence(amountCents, currentPlan.cadence, cadence)
      : amountCents
  return {
    key: `${tier.tier}-current`,
    tier: tier.tier,
    featureIds: currentPlan.featureIds ?? tier.featureIds,
    isCurrentPlan: true,
    displayName: legacy ? `Legacy ${names[tier.tier]}` : names[tier.tier],
    eyebrow: 'Your plan',
    isRecommended: false,
    offered: true,
    // The customer's own price, not the catalog tier's: a legacy rate and a catalog rate
    // are different products, so comparing them invents a discount.
    listAmountCents: restate(currentPlan.listAmountCents),
    shownAmountCents: restate(currentPlan.effectiveAmountCents),
    billedNote: projected
      ? `Billed ${formatCurrencyFromCents(currentPlan.effectiveAmountCents)}${CADENCE_SUFFIX[currentPlan.cadence]}`
      : null,
    isPromoRate: false,
  }
}

const targetColumn = (
  tier: PlanCatalogTier,
  target: PlanChangeTarget,
  eyebrow: string | null,
  isRecommended: boolean,
  names: TierDisplayNames
): TierColumn => ({
  key: target.lookupKey,
  tier: tier.tier,
  displayName: names[tier.tier],
  featureIds: tier.featureIds,
  isCurrentPlan: false,
  eyebrow: isRecommended ? RECOMMENDED_BADGE_COPY : eyebrow,
  isRecommended,
  offered: true,
  listAmountCents: target.listAmountCents,
  shownAmountCents:
    target.promo?.discountedAmountCents ?? target.effectiveAmountCents,
  billedNote: null,
  isPromoRate: target.promo !== null,
})

const catalogColumn = (
  tier: PlanCatalogTier,
  cadence: PlanCadence,
  names: TierDisplayNames
): TierColumn => {
  const price = tier.prices.find((entry) => entry.cadence === cadence)
  return {
    key: tier.tier,
    tier: tier.tier,
    displayName: names[tier.tier],
    featureIds: tier.featureIds,
    isCurrentPlan: false,
    eyebrow: null,
    isRecommended: false,
    offered: price?.offered ?? false,
    listAmountCents: price?.listAmountCents ?? null,
    shownAmountCents: null,
    billedNote: null,
    isPromoRate: false,
  }
}

export const ComparisonTable = ({
  catalog,
  currentPlan,
  targets,
  cadence,
  hideRows = [],
  sCorpServiceFor,
  recommendedLookupKeys,
}: ComparisonTableProps) => {
  const names = useBaseTierDisplayNames()
  // A below-lineup tier has no catalog entry, so nothing else would emit the plan the
  // customer is actually on. Their own features carry the column.
  const offCatalogTier = !catalog.tiers.some(
    (tier) => tier.tier === currentPlan.tier
  )
  // A tier off the lineup has no catalog column to be told apart from, so the prefix would
  // distinguish it from nothing. Without their own feature set there is nothing truthful to
  // put in the column either: every row would read as not included.
  const ownColumn =
    offCatalogTier && currentPlan.featureIds
      ? currentPlanColumn(
          { tier: currentPlan.tier, featureIds: currentPlan.featureIds },
          currentPlan,
          cadence,
          false,
          names
        )
      : null

  const catalogColumns: TierColumn[] = [...catalog.tiers]
    .sort((a, b) => compareTierRank(a.tier, b.tier))
    .flatMap((tier) => {
      const target = targets.find(
        (candidate) =>
          candidate.tier === tier.tier && candidate.cadence === cadence
      )
      if (tier.tier !== currentPlan.tier) {
        return [
          target
            ? targetColumn(
                tier,
                target,
                null,
                recommendedLookupKeys?.has(target.lookupKey) ?? false,
                TIER_DISPLAY_NAMES
              )
            : catalogColumn(tier, cadence, TIER_DISPLAY_NAMES),
        ]
      }
      const legacy = isLegacyPlan(currentPlan, tier)
      const own = currentPlanColumn(
        tier,
        currentPlan,
        cadence,
        legacy && names[tier.tier] === TIER_DISPLAY_NAMES[tier.tier],
        names
      )
      if (!target) {
        return legacy
          ? [own, catalogColumn(tier, cadence, TIER_DISPLAY_NAMES)]
          : [own]
      }
      const eyebrow = legacy ? null : `${CADENCE_LABEL[cadence]} billing`
      return [
        own,
        targetColumn(
          tier,
          target,
          eyebrow,
          recommendedLookupKeys?.has(target.lookupKey) ?? false,
          TIER_DISPLAY_NAMES
        ),
      ]
    })

  const columns = ownColumn ? [ownColumn, ...catalogColumns] : catalogColumns
  const audienceEyebrows = useAudienceEyebrows(
    columns.map((column) => column.tier)
  )
  const showAudienceRow = Object.keys(audienceEyebrows).length > 0

  // Cheapest tier's features first, so a column reads as additive down the lineup.
  const rows = displayRowsFor(
    columns.map((column) => column.featureIds),
    { hide: hideRows }
  )

  const anyBelowList = columns.some(strikesList)
  const anyPromo = columns.some((column) => column.isPromoRate)
  const anyProjected = columns.some((column) => column.billedNote !== null)

  const notes: string[] = []
  if (anyPromo) {
    notes.push(
      'A promotional rate is introductory. The struck-through figure is the rate it returns to.'
    )
  } else if (anyBelowList) {
    notes.push(
      'Prices are your rate, with your discount applied. Struck-through figures are list.'
    )
  }

  return (
    <div
      data-testid="plan-comparison-table"
      className="@container overflow-hidden"
    >
      <div className="bg-neutral-0 px-2 py-4">
        <Text variant={TypographyStyles.H2}>Plan comparison</Text>
      </div>

      {/* Positioned so the cells' sr-only text is clipped by the scroller, not the page. */}
      <div className="relative overflow-x-auto">
        <Table
          className={mtc('table-fixed', TABLE_MIN_WIDTH)}
          // React's style type has no custom properties; the value is a plain number
          style={{ '--plan-columns': columns.length } as CSSProperties}
        >
          <Table.Header>
            <Table.Row className={mtc(showAudienceRow && 'border-b-0')}>
              <Table.HeaderCell
                className={mtc(
                  STICKY_FEATURE_CELL,
                  'w-[140px] bg-neutral-0 @xl:w-[280px]'
                )}
              >
                Feature
              </Table.HeaderCell>
              {columns.map((column) => (
                <Table.HeaderCell
                  key={column.key}
                  className={mtc('p-3', column.isCurrentPlan && currentTint)}
                >
                  <div className="flex w-full flex-col items-start gap-1 text-left">
                    <Text
                      variant={TypographyStyles.EYEBROW}
                      aria-hidden={!column.eyebrow}
                      className={mtc(
                        column.isRecommended
                          ? 'text-green-100'
                          : 'text-neutral-500',
                        !column.eyebrow && 'invisible'
                      )}
                    >
                      {column.eyebrow ?? '\u00a0'}
                    </Text>
                    <Text variant={TypographyStyles.LABEL_MD}>
                      {column.displayName}
                    </Text>
                  </div>
                </Table.HeaderCell>
              ))}
            </Table.Row>
            {showAudienceRow && (
              <Table.Row>
                <Table.HeaderCell
                  className={mtc(STICKY_FEATURE_CELL, 'bg-neutral-0')}
                >
                  <span className="sr-only">Who the plan is for</span>
                </Table.HeaderCell>
                {columns.map((column) => (
                  <Table.HeaderCell
                    key={column.key}
                    className={mtc(
                      'px-3 pb-3 pt-0 align-top',
                      column.isCurrentPlan && currentTint
                    )}
                  >
                    <Text
                      variant={TypographyStyles.EYEBROW}
                      className="w-full text-left"
                    >
                      {audienceEyebrows[column.tier] ?? '\u00a0'}
                    </Text>
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            )}
          </Table.Header>

          <Table.Body>
            {rows.map((row) => (
              <Table.Row key={row.key}>
                <Table.Cell
                  className={mtc(STICKY_FEATURE_CELL, 'bg-neutral-0')}
                >
                  {row.label}
                </Table.Cell>
                {columns.map((column) => {
                  const included = row.holds(new Set(column.featureIds))
                  const supersededByDedicatedSupport =
                    !included &&
                    row.key === ProductFeatureId.phoneSupport &&
                    column.featureIds.includes(
                      ProductFeatureId.dedicatedSupport
                    )
                  return (
                    <Table.Cell
                      key={column.key}
                      className={mtc(
                        'p-3',
                        column.isCurrentPlan && currentTint
                      )}
                    >
                      {!supersededByDedicatedSupport && (
                        <div className="flex justify-center">
                          <Icon
                            icon={included ? faCheck : faMinus}
                            color={
                              included ? 'text-green-100' : 'text-neutral-300'
                            }
                          />
                          <span className="sr-only">
                            {included ? 'Included' : 'Not included'}
                          </span>
                        </div>
                      )}
                    </Table.Cell>
                  )
                })}
              </Table.Row>
            ))}

            {sCorpServiceFor !== undefined &&
              columns.some((column) =>
                sCorpServiceFor(column.tier, column.isCurrentPlan)
              ) && (
                <Table.Row>
                  <Table.Cell
                    className={mtc(
                      STICKY_FEATURE_CELL,
                      'bg-neutral-0 align-top'
                    )}
                  >
                    S corp support
                  </Table.Cell>
                  {columns.map((column) => {
                    const service =
                      sCorpServiceFor?.(column.tier, column.isCurrentPlan) ??
                      null
                    return (
                      <Table.Cell
                        key={column.key}
                        className={mtc(
                          'p-3 align-top',
                          column.isCurrentPlan && currentTint
                        )}
                      >
                        {service ? (
                          <Text
                            variant={TypographyStyles.BODY_SM}
                            className="text-left"
                          >
                            {planChangeFeatureLabel(service)}
                          </Text>
                        ) : (
                          <div className="flex justify-center">
                            <Icon icon={faMinus} color="text-neutral-300" />
                            <span className="sr-only">Not included</span>
                          </div>
                        )}
                      </Table.Cell>
                    )
                  })}
                </Table.Row>
              )}

            <Table.Row>
              <Table.Cell className={mtc(STICKY_FEATURE_CELL, 'bg-neutral-25')}>
                <div className="flex flex-col gap-0.5">
                  <Text
                    variant={TypographyStyles.EYEBROW}
                    className="text-neutral-900"
                  >
                    Price
                  </Text>
                  <Text
                    variant={TypographyStyles.BODY_XS}
                    className="text-neutral-500"
                  >
                    {`Shown per ${CADENCE_PERIOD[cadence]}`}
                  </Text>
                </div>
              </Table.Cell>
              {columns.map((column) => (
                <Table.Cell
                  key={column.key}
                  className={mtc(
                    'bg-neutral-25 p-3',
                    column.isCurrentPlan && 'bg-neutral-50'
                  )}
                >
                  <div className="flex flex-col items-start gap-0.5">
                    {!column.offered && !column.isCurrentPlan ? (
                      <Text
                        variant={TypographyStyles.EYEBROW}
                        className="text-neutral-400"
                      >
                        Not offered
                      </Text>
                    ) : (
                      <Text
                        variant={TypographyStyles.LABEL_MD}
                        className="whitespace-nowrap"
                      >
                        {formatCurrencyFromCents(
                          column.shownAmountCents ?? column.listAmountCents ?? 0
                        )}
                        {CADENCE_SUFFIX[cadence]}
                      </Text>
                    )}

                    {anyBelowList &&
                      (strikesList(column) &&
                      column.listAmountCents !== null ? (
                        <Text
                          variant={TypographyStyles.BODY_XS}
                          className="line-through text-neutral-400"
                        >
                          {formatCurrencyFromCents(column.listAmountCents)}
                          {CADENCE_SUFFIX[cadence]}
                        </Text>
                      ) : (
                        <PriceRowSpacer />
                      ))}

                    {anyProjected &&
                      (column.billedNote ? (
                        <Text
                          variant={TypographyStyles.BODY_XS}
                          className="text-neutral-500"
                        >
                          {column.billedNote}
                        </Text>
                      ) : (
                        <PriceRowSpacer />
                      ))}
                  </div>
                </Table.Cell>
              ))}
            </Table.Row>
          </Table.Body>
        </Table>
      </div>

      {notes.length > 0 && (
        <div className="border-t border-neutral-100 bg-neutral-25 px-2 py-3">
          {notes.map((note) => (
            <Text
              key={note}
              variant={TypographyStyles.BODY_SM}
              className="text-neutral-500"
            >
              {note}
            </Text>
          ))}
        </div>
      )}
    </div>
  )
}
